import * as React from "react";
import { Button as AtlasButton } from "@wellsky/atlas-ui-react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background text-foreground hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-card border border-primary text-secondary-foreground hover:bg-muted",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

// Map shadcn variants to atlas-ui variants
const ATLAS_VARIANT_MAP: Record<string, { variant: "contained" | "outlined" | "flat"; intent?: "default" | "destructive" }> = {
  default: { variant: "contained" },
  destructive: { variant: "contained", intent: "destructive" },
  outline: { variant: "outlined" },
  secondary: { variant: "outlined" },
  ghost: { variant: "flat" },
  link: { variant: "flat" },
};

const ATLAS_SIZE_MAP: Record<string, "sm" | "md" | "lg"> = {
  default: "md",
  sm: "sm",
  lg: "lg",
  icon: "sm",
};

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants>) {
  const atlasMapping = ATLAS_VARIANT_MAP[variant ?? "default"] ?? { variant: "contained" as const };
  const atlasSize = ATLAS_SIZE_MAP[size ?? "default"] ?? "md";

  return (
    <AtlasButton
      data-slot="button"
      variant={atlasMapping.variant}
      intent={atlasMapping.intent}
      size={atlasSize}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
